import { ChevronRight, Image, Map as MapIcon } from 'lucide-react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useScreens } from '../hooks/useCodeMap.js';
import type { ScreenNode, ScreenStatus } from '../types/code-map.js';

/** @graph-connects none */
function RoutePath({ route }: { route: string }) {
  const parts = route.split('/');
  return (
    <>
      {parts.map((seg, i) => (
        <Fragment key={`${seg}-${String(i)}`}>
          {i > 0 ? '/' : ''}
          {/^\[.*\]$/.test(seg) ? <span className="cm-dyn">{seg}</span> : seg}
        </Fragment>
      ))}
    </>
  );
}

/** @graph-connects none */
function formatDate(iso: string | null): string {
  if (!iso) {
    return '—';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  return d.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

interface TreeNodeProps {
  node: ScreenNode;
  depth: number;
  selectedRoute: string | null;
  collapsed: Set<string>;
  onToggle: (routePath: string) => void;
  onSelect: (node: ScreenNode) => void;
}

/** @graph-connects none */
function TreeNode({ node, depth, selectedRoute, collapsed, onToggle, onSelect }: TreeNodeProps) {
  const { t } = useTranslation();
  const hasChildren = node.children.length > 0;
  const isCollapsed = collapsed.has(node.routePath);
  const isSelected = node.routePath === selectedRoute && !node.isGroup;

  return (
    <li className={`${hasChildren ? '' : 'cm-leaf'} ${isCollapsed ? 'cm-collapsed' : ''}`}>
      <button
        type="button"
        className={`cm-node ${isSelected ? 'cm-sel' : ''}`}
        aria-expanded={hasChildren ? !isCollapsed : undefined}
        onClick={() => {
          if (node.isGroup) {
            if (hasChildren) {
              onToggle(node.routePath);
            }
            return;
          }
          onSelect(node);
        }}
      >
        <span
          className="cm-twist"
          aria-hidden="true"
          onClick={(e) => {
            if (hasChildren) {
              e.stopPropagation();
              onToggle(node.routePath);
            }
          }}
        >
          {hasChildren ? '▾' : ''}
        </span>
        <span className="cm-route">
          <RoutePath route={node.routePath} />
        </span>
        {!node.isGroup && node.statuses.length > 0 ? (
          <Badge variant="default" size="sm">
            {t('codeMap.statusCount', { num: String(node.statuses.length) })}
          </Badge>
        ) : null}
      </button>
      {hasChildren ? (
        <ul>
          {node.children.map((child) => (
            <TreeNode
              key={child.routePath}
              node={child}
              depth={depth + 1}
              selectedRoute={selectedRoute}
              collapsed={collapsed}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/** @graph-connects none */
function collectGroupRoutes(nodes: ScreenNode[], depth: number, acc: Set<string>): void {
  for (const node of nodes) {
    if (node.children.length > 0 && depth >= 2) {
      acc.add(node.routePath);
    }
    collectGroupRoutes(node.children, depth + 1, acc);
  }
}

/** @graph-connects none */
function collectAllGroupRoutes(nodes: ScreenNode[], acc: Set<string>): void {
  for (const node of nodes) {
    if (node.children.length > 0) {
      acc.add(node.routePath);
      collectAllGroupRoutes(node.children, acc);
    }
  }
}

/** @graph-connects none */
function StatusCard({ status }: { status: ScreenStatus }) {
  const { t } = useTranslation();
  return (
    <div className="cm-status-card">
      <div className="cm-status-thumb">
        {status.screenshotUrl ? (
          <img
            src={status.screenshotUrl}
            alt={t('codeMap.screenshotAlt', { label: status.label })}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <span className="text-[10px] text-[var(--color-text-muted)]">
              {t('codeMap.notCaptured')}
            </span>
          </div>
        )}
      </div>
      <div className="cm-status-label">
        <span>{status.label}</span>
      </div>
    </div>
  );
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business エアクロメンズ画面マップページ。左にroute階層ツリー、右に選択画面の詳細パネル（titleタグ・最終更新日・ステータス一覧）。
 * @graph-connects useScreens [calls] 画面ツリー取得フック
 */
export function CodeMapPage() {
  const { t } = useTranslation();
  const query = useScreens();

  const tree = useMemo<ScreenNode[]>(() => query.data?.tree ?? [], [query.data]);

  const [selected, setSelected] = useState<ScreenNode | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  useEffect(() => {
    const next = new Set<string>();
    collectGroupRoutes(tree, 0, next);
    setCollapsed(next);
  }, [tree]);

  const handleSelect = (node: ScreenNode): void => {
    setSelected(node);
  };

  const handleToggle = (routePath: string): void => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(routePath)) {
        next.delete(routePath);
      } else {
        next.add(routePath);
      }
      return next;
    });
  };

  const handleExpandAll = (): void => {
    setCollapsed(new Set());
  };

  const handleCollapseAll = (): void => {
    const next = new Set<string>();
    collectAllGroupRoutes(tree, next);
    setCollapsed(next);
  };

  const snapshot = query.data?.snapshot ?? null;
  const screenCount = query.data?.screenCount ?? 0;

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-8">
      <div className="mb-1 font-mono text-xs tracking-widest text-[var(--color-accent-primary)] uppercase">
        {t('codeMap.eyebrow')}
      </div>
      <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">{t('codeMap.title')}</h1>
      <p className="mt-2 text-sm leading-relaxed text-[var(--color-text-secondary)]">
        {t('codeMap.description')}
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Badge variant="default" size="md">
          {t('codeMap.screenCount', { num: screenCount.toLocaleString('ja-JP') })}
        </Badge>
        {snapshot ? (
          <span className="text-xs text-[var(--color-text-muted)]">
            {t('codeMap.latestSnapshot')}: {snapshot.snapshotDate}
            {snapshot.commitSha ? ` ・ ${snapshot.commitSha.slice(0, 7)}` : ''}
            {snapshot.generatedAt ? ` ・ ${snapshot.generatedAt}` : ''}
          </span>
        ) : null}
      </div>

      {query.isLoading ? (
        <p className="mt-8 text-sm text-[var(--color-text-muted)]">{t('app.loading')}</p>
      ) : query.isError ? (
        <Card className="mt-6 p-6">
          <p className="text-sm text-[var(--color-text-secondary)]">{t('codeMap.fetchError')}</p>
        </Card>
      ) : tree.length === 0 ? (
        <Card className="mt-6 p-8 text-center">
          <MapIcon className="mx-auto h-8 w-8 text-[var(--color-text-muted)]" aria-hidden="true" />
          <p className="mt-3 text-sm text-[var(--color-text-secondary)]">
            {t('codeMap.awaitingSnapshot')}
          </p>
        </Card>
      ) : (
        <div className="mt-6 grid grid-cols-1 items-start gap-5 lg:grid-cols-[1fr_minmax(360px,480px)]">
          <Card className="p-0">
            <div className="flex items-center gap-2 border-b border-dashed border-[var(--color-border)] px-4 py-2.5">
              <h2 className="text-xs font-semibold tracking-wide text-[var(--color-text-muted)] uppercase">
                {t('codeMap.treeHeader')}
              </h2>
              <div className="ml-auto flex gap-2">
                <Button variant="ghost" size="sm" onClick={handleExpandAll}>
                  {t('codeMap.expandAll')}
                </Button>
                <Button variant="ghost" size="sm" onClick={handleCollapseAll}>
                  {t('codeMap.collapseAll')}
                </Button>
              </div>
            </div>
            <ul className="cm-tree px-3 py-2 lg:max-h-[calc(100vh-13rem)] lg:overflow-y-auto">
              {tree.map((node) => (
                <TreeNode
                  key={node.routePath}
                  node={node}
                  depth={0}
                  selectedRoute={selected?.routePath ?? null}
                  collapsed={collapsed}
                  onToggle={handleToggle}
                  onSelect={handleSelect}
                />
              ))}
            </ul>
          </Card>

          <Card className="p-0 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
            {!selected ? (
              <div className="px-4 py-12 text-center">
                <ChevronRight
                  className="mx-auto h-6 w-6 text-[var(--color-text-muted)]"
                  aria-hidden="true"
                />
                <p className="mt-2 text-sm text-[var(--color-text-muted)]">
                  {t('codeMap.selectPrompt')}
                </p>
              </div>
            ) : (
              <div>
                <div className="border-b border-[var(--color-border)] px-4 py-3.5">
                  <h3 className="cm-path text-base font-bold text-[var(--color-text-primary)]">
                    <RoutePath route={selected.routePath} />
                  </h3>
                </div>

                <div className="space-y-4 px-4 py-4">
                  <div className="cm-detail-row">
                    <span className="cm-detail-label">{t('codeMap.pageTitle')}</span>
                    <span className="text-sm text-[var(--color-text-primary)]">
                      {selected.titleTag ?? '—'}
                    </span>
                  </div>

                  <div className="cm-detail-row">
                    <span className="cm-detail-label">{t('common.lastUpdated')}</span>
                    <span className="text-sm text-[var(--color-text-primary)]">
                      {formatDate(selected.lastUpdatedAt)}
                    </span>
                  </div>

                  <div className="cm-detail-row">
                    <span className="cm-detail-label">{t('codeMap.filePath')}</span>
                    <span className="cm-path text-sm break-all text-[var(--color-text-primary)]">
                      {selected.filePath ?? '—'}
                    </span>
                  </div>

                  <div>
                    <div className="cm-detail-label mb-2 flex items-center gap-1.5">
                      <Image
                        className="h-3.5 w-3.5 text-[var(--color-text-muted)]"
                        aria-hidden="true"
                      />
                      {t('codeMap.statusList')}
                      {selected.statuses.length > 0 ? (
                        <Badge variant="default" size="sm">
                          {String(selected.statuses.length)}
                        </Badge>
                      ) : null}
                    </div>
                    {selected.statuses.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-[var(--color-border)] px-4 py-6 text-center">
                        <p className="text-xs text-[var(--color-text-muted)]">
                          {t('codeMap.noStatus')}
                        </p>
                      </div>
                    ) : (
                      <div className="cm-status-grid">
                        {selected.statuses.map((status) => (
                          <StatusCard key={status.key} status={status} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
